import * as React from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { useNotificationsFeed } from "@/api/notifications";
import { rowHref } from "@/lib/notificationRoute";
import { useAuthStore } from "@/store/authStore";
import { MAX_TOASTS, alarmKey, clearedAt, freshUrgent, noticeDuration, noticeText, urgency } from "./alarmNotices";

/**
 * MV2-1240 — alarms that arrive while the page is open, without a reload.
 * Rides the bell's own 30 s poll (transport policy, CROSS_PAGE §16): new
 * critical and major alarm notices toast, the alarm lists refresh, and a
 * toast whose alarm clears is taken down. Nothing toasts on page load or on
 * entering a tenant — only what arrives after. Returns the unread urgent
 * count the bell colours its badge by.
 */
export function useAlarmNotices(): { urgent: number } {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const actingAs = useAuthStore((s) => s.actingAs?.clientId ?? null);
  const feed = useNotificationsFeed({ tab: "alarms", limit: 20 }, { refetchInterval: 30_000 });
  const rows = React.useMemo(() => feed.data?.items ?? [], [feed.data]);
  const seen = React.useRef<Set<string> | null>(null);
  const tenant = React.useRef(actingAs);
  const shown = React.useRef(new Set<string>());

  React.useEffect(() => {
    if (!feed.data) return;
    // First read, or a new tenant: what is already there is not news.
    if (seen.current === null || tenant.current !== actingAs) {
      seen.current = new Set(rows.map((r) => r.id));
      tenant.current = actingAs;
      return;
    }
    const fresh = freshUrgent(rows, seen.current);
    for (const r of rows) seen.current.add(r.id);
    // An alarm that cleared takes its toast down.
    for (const r of rows) {
      const key = alarmKey(r);
      if (clearedAt(r) && shown.current.has(key)) {
        toast.dismiss(key);
        shown.current.delete(key);
      }
    }
    if (!fresh.length) return;
    void qc.invalidateQueries({ queryKey: ["alarms"] });
    void qc.invalidateQueries({ queryKey: ["alarm-detail"] });
    if (fresh.length > MAX_TOASTS) {
      const critical = fresh.some((r) => urgency(r) === "critical");
      (critical ? toast.error : toast.warning)(`${fresh.length} new alarms`, {
        id: "alarm:burst",
        description: critical ? "at least one is critical" : "major and above",
        duration: critical ? Infinity : 10_000,
        action: { label: "Open", onClick: () => navigate("/notifications?tab=alarms") },
      });
      return;
    }
    for (const r of fresh) {
      const level = urgency(r)!;
      const { title, description } = noticeText(r);
      const key = alarmKey(r);
      shown.current.add(key);
      (level === "critical" ? toast.error : toast.warning)(title, {
        id: key,
        description,
        duration: noticeDuration(level),
        action: { label: "Open", onClick: () => navigate(rowHref(r)) },
      });
    }
  }, [feed.data, rows, actingAs, navigate, qc]);

  const urgent = rows.filter((r) => !r.read_at && !r.dismissed_at && urgency(r) !== null).length;
  return { urgent };
}
